import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import isFunction from "lodash/isFunction";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useElectronAPI } from "@circle-react/hooks/utils/useElectronAPI";
import { Modal as ModalV2 } from "@circle-react-uikit/ModalV2";
import type {
  ModalAnimationType,
  ModalPositionType,
  ModalSizeType,
} from "@circle-react-uikit/ModalV2/ModalContent";
import { ModalCloseButton } from "./ModalCloseButton";

export interface ModalProps {
  animations?: ModalAnimationType;
  children?: ReactNode;
  className?: string;
  contentPortalId?: string;
  isOpen: boolean;
  onClose: () => void;
  position?: ModalPositionType;
  shouldCloseOnOutsideClick?: boolean;
  shouldShowCloseButton?: boolean;
  size?: ModalSizeType;
}

export const Modal = ({
  isOpen,
  onClose,
  contentPortalId,
  children,
  shouldShowCloseButton,
  className = "",
  animations = "slideBottomUp",
  size = "screen-with-header",
  position = "default",
  shouldCloseOnOutsideClick = false,
}: ModalProps) => {
  const isSmScreen = useSmScreenMediaQuery();
  const { onModalOpened, onModalClosed } = useElectronAPI();
  // This snippet triggers the animation when this modal
  // is rendered with isModalOpen === true
  const [isModalOpen, setIsModalOpen] = useState(false);
  useEffect(() => {
    setIsModalOpen(isOpen);
    if (isOpen) {
      onModalOpened();
    } else {
      onModalClosed();
    }
  }, [isOpen, setIsModalOpen, onModalOpened, onModalClosed]);

  return (
    <ModalV2
      isOpen={isModalOpen}
      onClose={() => {
        if (isFunction(onClose)) {
          onClose();
        }
      }}
      contentPortalId={contentPortalId}
      shouldShowCloseButton={shouldShowCloseButton}
    >
      <ModalV2.Overlay />
      {shouldShowCloseButton && !isSmScreen && (
        <ModalCloseButton
          onClick={() => {
            if (isFunction(onClose)) {
              onClose();
            }
          }}
        />
      )}
      <ModalV2.Content
        size={size}
        animations={animations}
        className={className}
        position={position}
        isOpen={isOpen}
        shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
      >
        {children}
      </ModalV2.Content>
    </ModalV2>
  );
};
